import RequiredMark from './RequiredMark';
import React, { useState, useRef, useEffect, useLayoutEffect, useId } from 'react';
import { createPortal } from 'react-dom';
import {
    tableToolbarDropdownBtnClass,
    tableToolbarDropdownBtnActiveClass,
    tableToolbarControlOpenClass,
    tableToolbarIconClass,
    tableToolbarIconOpenClass,
} from '../lib/tableClasses';
import { REQUIRED_EMPTY_BORDER } from '../lib/fieldClasses';
import FilterChip from './FilterChip';
import { Icon } from './Icon';
import type { IconName } from './Icon';

export interface DropdownOption {
    value: string;
    label: string;
    /**
     * Optional section name. When set, a non-clickable section header is
     * rendered before the first option of each group. The caller MUST pass the
     * options already sorted by group (the list renders a header whenever an
     * option's group differs from the previous one). See Standards.md
     * §"DropdownSelect grouping".
     */
    group?: string;
    /** Optional secondary text below the label */
    sublabel?: string;
    /**
     * Optional Tailwind background class for a coloured leading dot rendered
     * before the label (e.g. `bg-emerald-500`, `bg-[#F0A04A]`). Used for status
     * pickers where each option has a semantic colour.
     */
    dotClass?: string;
    /**
     * Optional extra Tailwind classes applied to the label text (both trigger and dropdown).
     * Useful to color a subset of options (e.g. `'text-red-500'` for "not implemented" items).
     */
    labelClass?: string;
    /** Optional avatar image URL. Renders a small circular image before the label. */
    avatarUrl?: string | null;
    /** Override the avatar `<img>` shape/fit classes (default `rounded-full object-cover`).
     *  Use e.g. `rounded object-contain` for a wide logo/wordmark that must not be cropped. */
    avatarClass?: string;
    /**
     * Optional Material Symbols icon rendered in a circle the same size as
     * avatars. Useful for "all" / "none" options in avatar-based pickers so
     * the trigger button size stays consistent with selected user avatars.
     */
    leadingIcon?: IconName;
    /**
     * Optional Tailwind classes applied to the leading icon. When provided,
     * the icon is rendered "naked" (no grey circle wrapper) — used to give
     * each option its own semantic colour (e.g. material icons, brand badges).
     */
    leadingIconClass?: string;
    /**
     * Shown but not choosable: greyed out, skipped by click, Enter and Space.
     * For a choice that exists and is unavailable right now (an agent that is
     * offline), where hiding it would leave people wondering where it went.
     * Say WHY in `sublabel`.
     */
    disabled?: boolean;
}

interface DropdownSelectProps {
    /** When true, the trigger is rendered greyed-out and clicks are ignored */
    disabled?: boolean;
    /** Material Symbol icon name shown in the trigger button */
    icon?: IconName;
    /** Floating label above the trigger (for form-field use) */
    label?: string;
    /** Validation error shown below the trigger (also reds the trigger border) */
    error?: string;
    /** Red trigger border WITHOUT a message — for proactively flagging a
     *  required-but-empty field (pairs with the app's red-empty convention). */
    invalid?: boolean;
    /** Required field: a red asterisk after the label and `aria-required`
     *  (Standards §14.7). Not `required`, which on an input is the browser's
     *  own validation and blocks the form with its bubble. */
    markRequired?: boolean;
    /** Text shown when no option is selected */
    placeholder?: string;
    /** Currently selected value */
    value: string;
    /** Available options */
    options: DropdownOption[];
    /** Called when user picks an option */
    onChange: (value: string) => void;
    /** Show search input when there are many options */
    searchable?: boolean;
    /** Placeholder for the search input */
    searchPlaceholder?: string;
    /** Empty-state text when search yields no results */
    emptyText?: string;
    /** Extra class on the root wrapper */
    className?: string;
    /** Dropdown width — defaults to w-64 */
    dropdownWidth?: string;
    /**
     * Visual variant:
     * - `'field'` (default): trigger stays neutral regardless of value.
     *   Right for form fields that ALWAYS hold a value — keeps the border
     *   neutral so it matches sibling Inputs / Selects.
     * - `'filter'`: trigger fills with primary when an option is selected
     *   (the table-toolbar "filter applied" look — empty = neutral, set =
     *   highlighted). Pass this explicitly on table-header filter controls.
     *
     * Default flipped from `'filter'` to `'field'` in v2.30 — most form
     * usages had to defensively pass `variant='field'` to avoid the orange
     * "active filter" border bleeding into modals. Now the asymmetry runs
     * the other way: toolbar filters explicitly opt in to `'filter'`.
     */
    variant?: 'filter' | 'field' | 'chip';
    /**
     * `variant="chip"` only — the trigger becomes a `FilterChip` («Estado:
     * Pendientes»). `chipLabel` is the part before the colon.
     */
    chipLabel?: string;
    /** `variant="chip"` only — styles the chip as narrowing the list. Defaults to "has a non-empty value". */
    chipActive?: boolean;
    /** `variant="chip"` only — renders a ✕ that calls this. */
    onChipRemove?: () => void;
    chipRemoveLabel?: string;
    /** Open the list as soon as it mounts (a filter just added from «+ Filtro»). */
    autoOpen?: boolean;
    /** Told every time the list opens or closes. */
    onOpenChange?: (open: boolean) => void;
    /**
     * When true, options render text-only — no radio circle, no leading icon wrapper.
     * Useful for compact selectors where the label alone is enough (e.g. page-size).
     */
    hideOptionIndicator?: boolean;
    /**
     * Background tone for the trigger — overrides the default toolbar surface.
     * Use this when the dropdown sits next to ``Input`` fields (which default
     * to ``bg-transparent``) so all fields share the same surface and blend
     * with the parent (modal body, section, …).
     *
     * - undefined (default) → toolbar surface ``bg-white dark:bg-surface-dark/60``.
     *   Right for table toolbars where the dropdown should pop against the
     *   strip.
     * - `transparent` → ``bg-transparent``. Right for fields placed directly
     *   on a modal body or section — matches the standard "fields always
     *   transparent" rule (see ``Standards.md §Field backgrounds``).
     * - `elevated` → ``bg-slate-50/50 dark:bg-slate-800/50`` (layer-2 Tone B).
     *   Right for the rare case where you want fields to read as one shade
     *   lighter than the surrounding card.
     */
    surface?: 'transparent' | 'elevated';
    /**
     * When set, stamps ``data-testid={optionTestId}`` on every rendered option
     * button so E2E tests can target the (portalled) options — the styled options
     * are `<button>`s, not native `<option>`s, so they otherwise have no stable
     * hook. Opt-in; off by default.
     */
    optionTestId?: string;
    /**
     * Accessible name of the trigger when there is no visible `label`
     * (toolbar filters, the page-size picker). With a `label` the trigger is
     * labelled by it automatically.
     */
    ariaLabel?: string;
    /**
     * Which edge of the trigger the list lines up with. `start` (default) opens
     * to the right of the trigger's left edge; `end` lines its right edge up
     * with the trigger's, for a trigger that sits at the right of a card — a
     * list wider than it would otherwise run off the screen.
     */
    align?: 'start' | 'end';
    /** Let long `sublabel`s wrap instead of being cut with an ellipsis. */
    wrapSublabels?: boolean;
}

/**
 * Styled dropdown select — same visual pattern as TagSelector.
 * Use instead of native <Select> when you need a richer dropdown UI.
 *
 * @example
 * <DropdownSelect
 *     icon="language"
 *     placeholder="Idioma"
 *     value={locale}
 *     options={[{ value: 'es', label: 'Español' }, { value: 'en', label: 'English' }]}
 *     onChange={handleChange}
 * />
 */
const DropdownSelect: React.FC<DropdownSelectProps> = ({
    disabled = false,
    icon,
    label,
    markRequired = false,
    error,
    invalid = false,
    placeholder = 'Seleccionar',
    value,
    options,
    onChange,
    searchable = false,
    searchPlaceholder = 'Buscar...',
    emptyText = 'Sin resultados',
    className = '',
    dropdownWidth = 'w-64',
    align = 'start',
    wrapSublabels = false,
    variant = 'field',
    hideOptionIndicator = false,
    surface,
    optionTestId,
    ariaLabel,
    chipLabel,
    chipActive,
    onChipRemove,
    chipRemoveLabel,
    autoOpen = false,
    onOpenChange,
}) => {
    const [isOpen, setIsOpen] = useState(autoOpen && !disabled);
    const onOpenChangeRef = useRef(onOpenChange);
    onOpenChangeRef.current = onOpenChange;
    const openedOnce = useRef(false);
    useEffect(() => {
        // Skip the mount unless it mounted open: "closed" on mount is not news.
        if (!openedOnce.current && !isOpen) return;
        openedOnce.current = true;
        onOpenChangeRef.current?.(isOpen);
    }, [isOpen]);
    const [searchTerm, setSearchTerm] = useState('');
    const dropdownRef = useRef<HTMLDivElement>(null);
    // The trigger has its own ref. It used to be found with
    // `querySelector('button')` on the wrapper, which meant any <button>
    // rendered before it (a clear icon, a label action) would silently move
    // the panel of all 126 instances.
    const triggerRef = useRef<HTMLButtonElement>(null);
    const panelRef = useRef<HTMLDivElement>(null);
    // Keyboard navigation: the highlighted option, announced through
    // aria-activedescendant so focus can stay on the trigger (or the search
    // input) while the list is portalled to <body>.
    const [activeIndex, setActiveIndex] = useState(-1);
    const uid = useId();
    const labelId = `${uid}-label`;
    const panelId = `${uid}-listbox`;
    const optionId = (i: number) => `${uid}-opt-${i}`;
    const [panelRect, setPanelRect] = useState<{ top: number; left: number; right: number; width: number; flip: boolean } | null>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;
            if (
                dropdownRef.current && !dropdownRef.current.contains(target) &&
                panelRef.current && !panelRef.current.contains(target)
            ) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Clear the computed position when closed so a REOPEN never paints a frame
    // at the previous (stale) coordinates and then snaps — that was the visible
    // "flying into place". With this, the panel only renders once the fresh
    // position is computed in the layout effect below (which runs before paint).
    useEffect(() => {
        if (!isOpen) setPanelRect(null);
    }, [isOpen]);

    useLayoutEffect(() => {
        if (!isOpen || !dropdownRef.current) return;
        const update = () => {
            const trigger = triggerRef.current;
            if (!trigger) return;
            const rect = trigger.getBoundingClientRect();
            const spaceBelow = window.innerHeight - rect.bottom;
            const flip = spaceBelow < 260 && rect.top > 260;
            setPanelRect({
                top: flip ? rect.top - 8 : rect.bottom + 8,
                left: rect.left,
                right: window.innerWidth - rect.right,
                width: rect.width,
                flip,
            });
        };
        update();
        window.addEventListener('resize', update);
        window.addEventListener('scroll', update, true);
        return () => {
            window.removeEventListener('resize', update);
            window.removeEventListener('scroll', update, true);
        };
    }, [isOpen]);

    const selectedOption = options.find((o) => o.value === value);
    const buttonText = selectedOption?.label ?? placeholder;
    // Empty-string value counts as "no filter applied" — keeps the trigger
    // neutral (per documented `variant: 'filter'` convention) even when the
    // caller includes a `{ value: '', label: 'All …' }` option in the list.
    const hasValue = !!selectedOption && value !== '';

    const filtered = searchable && searchTerm
        ? options.filter((o) =>
              o.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
              o.value.toLowerCase().includes(searchTerm.toLowerCase()),
          )
        : options;

    const handleSelect = (optValue: string) => {
        if (options.find((o) => o.value === optValue)?.disabled) return;
        onChange(optValue);
        setIsOpen(false);
        setSearchTerm('');
    };

    // Highlight follows the selection when the list opens, and the first match
    // while searching; keep it inside the visible part of the list.
    useEffect(() => {
        if (!isOpen) { setActiveIndex(-1); return; }
        const idx = filtered.findIndex((o) => o.value === value);
        setActiveIndex(idx >= 0 ? idx : (filtered.length ? 0 : -1));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, searchTerm]);
    useEffect(() => {
        if (!isOpen || activeIndex < 0) return;
        document.getElementById(optionId(activeIndex))?.scrollIntoView({ block: 'nearest' });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeIndex, isOpen]);

    const closeAndRefocus = () => {
        setIsOpen(false);
        setSearchTerm('');
        triggerRef.current?.focus();
    };

    // One handler on the root: React events bubble through the portal, so keys
    // pressed in the search input arrive here too.
    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (disabled) return;
        const onTrigger = e.target === triggerRef.current;
        if (!isOpen) {
            if (onTrigger && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
                e.preventDefault();
                setIsOpen(true);
            }
            return;
        }
        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setActiveIndex((i) => (filtered.length ? Math.min(i + 1, filtered.length - 1) : -1));
                break;
            case 'ArrowUp':
                e.preventDefault();
                setActiveIndex((i) => (filtered.length ? Math.max(i - 1, 0) : -1));
                break;
            case 'Home':
                e.preventDefault();
                setActiveIndex(filtered.length ? 0 : -1);
                break;
            case 'End':
                e.preventDefault();
                setActiveIndex(filtered.length ? filtered.length - 1 : -1);
                break;
            case 'Enter':
                e.preventDefault();
                if (activeIndex >= 0 && filtered[activeIndex]) { handleSelect(filtered[activeIndex].value); triggerRef.current?.focus(); }
                break;
            case ' ':
                // Space types in the search input; on the trigger it selects.
                if (onTrigger) {
                    e.preventDefault();
                    if (activeIndex >= 0 && filtered[activeIndex]) { handleSelect(filtered[activeIndex].value); }
                }
                break;
            case 'Escape':
                e.preventDefault();
                e.stopPropagation();
                closeAndRefocus();
                break;
            case 'Tab':
                setIsOpen(false);
                setSearchTerm('');
                break;
            default:
                break;
        }
    };

    return (
        <div className={`relative ${variant === 'chip' ? 'inline-flex min-w-0 shrink-0' : ''} ${label ? 'pt-2' : ''} ${className}`} ref={dropdownRef} onKeyDown={handleKeyDown}>
            {label && (
                // Truncates on a narrow control, so it carries its own text as
                // a tooltip — which needs it to receive the pointer (see
                // `Input`). It sits above the trigger, never over it.
                <span
                    id={labelId}
                    title={typeof label === 'string' ? label : undefined}
                    className="absolute top-0 left-2 px-1 bg-white dark:bg-surface-dark text-[10px] font-bold text-primary uppercase tracking-wider z-10 truncate max-w-[calc(100%-1rem)]"
                >
                    {label}
                    {markRequired && <> <RequiredMark /></>}
                </span>
            )}
            {/* Trigger button */}
            {variant === 'chip' ? (
                <FilterChip
                    ref={triggerRef}
                    label={chipLabel ?? ariaLabel ?? placeholder}
                    value={buttonText}
                    icon={icon}
                    active={chipActive ?? hasValue}
                    open={isOpen}
                    onRemove={onChipRemove}
                    removeLabel={chipRemoveLabel}
                    disabled={disabled}
                    role="combobox"
                    aria-haspopup="listbox"
                    aria-expanded={isOpen}
                    aria-controls={isOpen ? panelId : undefined}
                    aria-activedescendant={isOpen && activeIndex >= 0 ? optionId(activeIndex) : undefined}
                    aria-label={`${chipLabel ?? ariaLabel ?? placeholder}: ${buttonText}`}
                    onClick={() => !disabled && setIsOpen(!isOpen)}
                />
            ) : (
                <button
                    ref={triggerRef}
                    type="button"
                    disabled={disabled}
                    role="combobox"
                    aria-haspopup="listbox"
                    aria-expanded={isOpen}
                    aria-controls={isOpen ? panelId : undefined}
                    aria-activedescendant={isOpen && activeIndex >= 0 ? optionId(activeIndex) : undefined}
                    aria-labelledby={label ? labelId : undefined}
                    aria-required={markRequired || undefined}
                    aria-label={!label ? (ariaLabel ?? placeholder) : undefined}
                    aria-invalid={(invalid || error) ? true : undefined}
                    onClick={() => !disabled && setIsOpen(!isOpen)}
                    className={[
                        tableToolbarDropdownBtnClass,
                        // Surface override: drop the toolbar bg when the caller
                        // needs the dropdown to blend with sibling form fields
                        // (which default to ``bg-transparent``).
                        surface === 'transparent'
                            ? '!bg-transparent dark:!bg-transparent'
                            : surface === 'elevated'
                                ? '!bg-slate-50/50 dark:!bg-slate-800/50'
                                : '',
                        variant === 'filter' && hasValue ? tableToolbarDropdownBtnActiveClass : '',
                        isOpen ? tableToolbarControlOpenClass : '',
                        disabled ? 'opacity-50 cursor-not-allowed' : '',
                        (invalid || error) ? REQUIRED_EMPTY_BORDER : '',
                    ]
                        .filter(Boolean)
                        .join(' ')}
                >
                    {selectedOption?.avatarUrl && (
                        <img src={selectedOption.avatarUrl} alt="" className={`w-5 h-5 shrink-0 ${selectedOption.avatarClass ?? 'rounded-full object-cover'}`} />
                    )}
                    {selectedOption?.leadingIcon && !selectedOption?.avatarUrl && (
                        selectedOption.leadingIconClass ? (
                            <Icon name={selectedOption.leadingIcon} className={`!text-[18px] shrink-0 ${selectedOption.leadingIconClass}`} />
                        ) : (
                            <span className="w-5 h-5 rounded-full shrink-0 flex items-center justify-center bg-slate-200 dark:bg-slate-700">
                                <Icon name={selectedOption.leadingIcon} className="!text-[12px] text-slate-500 dark:text-slate-400" />
                            </span>
                        )
                    )}
                    {selectedOption?.dotClass && !selectedOption?.avatarUrl && !selectedOption?.leadingIcon && (
                        <span className={`w-2 h-2 rounded-full shrink-0 ${selectedOption.dotClass}`} />
                    )}
                    {icon && (
                        <Icon name={icon} className={`${tableToolbarIconClass} ${
                                variant === 'filter' && hasValue ? '!text-white' : isOpen ? tableToolbarIconOpenClass : ''
                            }`} />
                    )}
                    <span
                        // The chosen option truncates too («Por un…»): same
                        // rule as the label, its text is its own tooltip.
                        title={typeof buttonText === 'string' ? buttonText : undefined}
                        className={`min-w-0 flex-1 truncate text-left ${
                            variant === 'filter' && hasValue ? '!text-white' : (selectedOption?.labelClass || '')
                        }`}
                    >
                        {buttonText}
                    </span>
                    <Icon name="expand_more" className={`${tableToolbarIconClass} shrink-0 transition-transform ${
                            isOpen ? 'rotate-180' : ''
                        } ${variant === 'filter' && hasValue ? '!text-white' : isOpen ? tableToolbarIconOpenClass : ''}`} aria-hidden="true" />
                </button>
            )}

            {error && <p role="alert" className="text-red-500 text-xs mt-1 font-medium">{error}</p>}

            {/* Dropdown — portalled so it escapes modal/overflow clipping */}
            {isOpen && panelRect && createPortal(
                <div
                    ref={panelRef}
                    data-dropdown-portal=""
                    id={panelId}
                    role="listbox"
                    aria-labelledby={label ? labelId : undefined}
                    aria-required={markRequired || undefined}
                    aria-label={!label ? (ariaLabel ?? placeholder) : undefined}
                    style={{
                        position: 'fixed',
                        top: panelRect.flip ? undefined : panelRect.top,
                        bottom: panelRect.flip ? window.innerHeight - panelRect.top : undefined,
                        left: align === 'end' ? undefined : panelRect.left,
                        right: align === 'end' ? panelRect.right : undefined,
                        // Lined up on the right, a panel wider than the space to
                        // the trigger's left would start off-screen on a phone.
                        maxWidth: align === 'end' ? panelRect.left + panelRect.width - 8 : undefined,
                        width: dropdownWidth === 'w-full' ? panelRect.width : undefined,
                    }}
                    className={`z-[200] ${dropdownWidth !== 'w-full' ? dropdownWidth : ''} ${panelRect.flip ? 'origin-bottom' : 'origin-top'} bg-white dark:bg-surface-dark rounded-xl shadow-xl border border-slate-200 dark:border-slate-700 overflow-hidden animate-in fade-in zoom-in-95 duration-200`}
                >
                    {/* Search input */}
                    {searchable && (
                        <div className="p-2 border-b border-slate-100 dark:border-slate-800">
                            <input
                                type="text"
                                placeholder={searchPlaceholder}
                                aria-label={searchPlaceholder}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full px-3 py-1.5 bg-slate-50 dark:bg-surface-dark border border-slate-200 dark:border-slate-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 text-black dark:text-white"
                                autoFocus
                            />
                        </div>
                    )}

                    {/* Option list */}
                    <div className="max-h-60 overflow-y-auto p-1.5">
                        {filtered.length === 0 ? (
                            <div className="p-3 text-center text-xs text-slate-400">
                                {emptyText}
                            </div>
                        ) : (
                            <div className="flex flex-col gap-0.5">
                                {filtered.map((opt, idx) => {
                                    const isSelected = opt.value === value;
                                    const showHeader = !!opt.group && opt.group !== filtered[idx - 1]?.group;
                                    return (
                                        <React.Fragment key={opt.value}>
                                        {showHeader && (
                                            <div className="px-3 pt-2 pb-1 text-[10px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500 select-none">
                                                {opt.group}
                                            </div>
                                        )}
                                        <button
                                            type="button"
                                            role="option"
                                            id={optionId(idx)}
                                            aria-selected={isSelected}
                                            aria-disabled={opt.disabled || undefined}
                                            disabled={opt.disabled}
                                            tabIndex={-1}
                                            data-testid={optionTestId}
                                            onClick={() => handleSelect(opt.value)}
                                            onMouseMove={() => { if (activeIndex !== idx) setActiveIndex(idx); }}
                                            className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-left transition-colors ${
                                                opt.disabled
                                                    ? 'opacity-50 cursor-not-allowed text-slate-700 dark:text-slate-300'
                                                    : isSelected
                                                    ? 'bg-primary/10 text-primary font-medium'
                                                    : 'hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300'
                                            } ${idx === activeIndex && !isSelected && !opt.disabled ? 'bg-slate-100 dark:bg-slate-800' : ''}`}
                                        >
                                            {hideOptionIndicator ? null : opt.avatarUrl ? (
                                                <div className="relative shrink-0">
                                                    <img src={opt.avatarUrl} alt="" className={`w-6 h-6 ${opt.avatarClass ?? 'rounded-full object-cover'}`} />
                                                    {isSelected && (
                                                        <span className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full bg-primary flex items-center justify-center">
                                                            <Icon name="check" className="text-white !text-[10px]" />
                                                        </span>
                                                    )}
                                                </div>
                                            ) : opt.leadingIcon ? (
                                                opt.leadingIconClass ? (
                                                    <Icon name={opt.leadingIcon} className={`!text-[20px] shrink-0 ${opt.leadingIconClass}`} />
                                                ) : (
                                                    <div className="relative shrink-0">
                                                        <span className="w-6 h-6 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center">
                                                            <Icon name={opt.leadingIcon} className="!text-[14px] text-slate-500 dark:text-slate-400" />
                                                        </span>
                                                        {isSelected && (
                                                            <span className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full bg-primary flex items-center justify-center">
                                                                <Icon name="check" className="text-white !text-[10px]" />
                                                            </span>
                                                        )}
                                                    </div>
                                                )
                                            ) : opt.dotClass ? (
                                                <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${opt.dotClass}`} />
                                            ) : (
                                                <div
                                                    className={`w-4 h-4 shrink-0 rounded-full flex items-center justify-center border transition-colors ${
                                                        isSelected
                                                            ? 'bg-primary border-primary'
                                                            : 'border-slate-300 dark:border-slate-600'
                                                    }`}
                                                >
                                                    {isSelected && (
                                                        <Icon name="check" className="text-white !text-[12px]" />
                                                    )}
                                                </div>
                                            )}
                                            <div className="flex flex-col min-w-0">
                                                <span className={`truncate ${opt.labelClass || ''}`}>{opt.label}</span>
                                                {opt.sublabel && (
                                                    <span className={`text-[10px] text-slate-400 dark:text-slate-500 ${wrapSublabels ? 'whitespace-normal' : 'truncate'}`}>
                                                        {opt.sublabel}
                                                    </span>
                                                )}
                                            </div>
                                        </button>
                                        </React.Fragment>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>,
                document.body,
            )}
        </div>
    );
};

export default DropdownSelect;
